import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CalendarBlank, Users, SignOut, Trash, ClipboardText, CurrencyEur, Receipt, ChartBar, List as ListIcon, EnvelopeSimple, PaperPlaneTilt, FileText, Paperclip, FolderOpen, DownloadSimple, File, FilePdf, FileXls, FileDoc, FileImage, FileZip } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { LOGO_URL, DAYS, PLANNER_DAYS, TIME_SLOTS, timeToSlotIndex } from './teacher/constants';
import { ScheduleGrid } from './teacher/ScheduleGrid';
import { StudentsList } from './teacher/StudentsList';
import { LessonsList } from './teacher/LessonsList';
import { AttendanceView } from './teacher/AttendanceView';
import { PaymentsView } from './teacher/PaymentsView';
import { ReceiptsView } from './teacher/ReceiptsView';
import { StatisticsView } from './teacher/StatisticsView';

export default function TeacherDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('schedule');
  const scheduleGridRef = useRef(null);
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [attendanceTotal, setAttendanceTotal] = useState(0);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  // Email states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLog, setEmailLog] = useState({ records: [], total: 0 });
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [sendEmailStudent, setSendEmailStudent] = useState(null);
  const [sendEmailMode, setSendEmailMode] = useState('free');
  const [sendEmailForm, setSendEmailForm] = useState({ subject: '', body: '', template_id: '', send_to_both: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendEmailStep, setSendEmailStep] = useState('compose');

  // Form states
  const [lessonForm, setLessonForm] = useState({ student_id: '', day: 0, start_time: '13:00', end_time: '13:30' });
  const [availabilityDay, setAvailabilityDay] = useState(0);
  const [disabledSlots, setDisabledSlots] = useState([]);

  const loadData = useCallback(async () => {
    if (!selectedYear) return;
    try {
      const [lessonsRes, studentsRes, availRes, allStudentsRes] = await Promise.all([
        api.getLessons({ anno: selectedYear }),
        api.getStudents({ anno: selectedYear }),
        api.getAvailability(),
        api.getAllStudentsForLesson()
      ]);
      setLessons(lessonsRes.data);
      setStudents(studentsRes.data);
      setAllStudents(allStudentsRes.data.sort((a, b) => a.name.localeCompare(b.name, 'it')));
      const availMap = {};
      availRes.data.forEach(a => { availMap[a.day] = a.disabled_slots; });
      setAvailability(availMap);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { api.getTeacherAvailableYears().then(res => { if (res.data.length > 0) { setAvailableYears(res.data); setSelectedYear(res.data[res.data.length - 1]); } else { setSelectedYear(new Date().getFullYear()); setAvailableYears([new Date().getFullYear()]); } }).catch(() => { setSelectedYear(new Date().getFullYear()); setAvailableYears([new Date().getFullYear()]); }); }, []);

  // Email functions
  useEffect(() => {
    if (activeTab === 'email') {
      api.getTeacherEmailTemplates().then(res => setEmailTemplates(res.data)).catch(() => {});
      setEmailLogLoading(true);
      api.getTeacherEmailLog({ page: 1, limit: 100 }).then(res => setEmailLog(res.data)).catch(() => {}).finally(() => setEmailLogLoading(false));
    }
  }, [activeTab]);

  const openTeacherSendEmail = (student) => {
    setSendEmailStudent(student);
    setSendEmailMode('free');
    setSendEmailForm({ subject: '', body: '', template_id: '', send_to_both: false });
    setSendEmailStep('compose');
    setShowSendEmailModal(true);
  };

  const handleTeacherSendEmail = async () => {
    setSendingEmail(true);
    try {
      const payload = { student_id: sendEmailStudent.id, send_to_both: sendEmailForm.send_to_both };
      if (sendEmailMode === 'template' && sendEmailForm.template_id) {
        payload.template_id = sendEmailForm.template_id;
      } else {
        payload.subject = sendEmailForm.subject;
        payload.body = sendEmailForm.body;
      }
      const res = await api.teacherSendEmail(payload);
      toast.success(res.data.message);
      setShowSendEmailModal(false);
      api.getTeacherEmailLog({ page: 1, limit: 100 }).then(res => setEmailLog(res.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio email');
    } finally { setSendingEmail(false); }
  };



  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Schedule callbacks
  const handleCellClick = (day, time, endTime) => {
    setEditingLesson(null);
    setLessonForm({ student_id: '', day, start_time: time, end_time: endTime });
    setShowLessonModal(true);
  };

  const openEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({ student_id: lesson.student_id, day: lesson.day, start_time: lesson.start_time, end_time: lesson.end_time });
    setShowLessonModal(true);
  };

  const handleSaveLesson = async (e) => {
    e.preventDefault();
    try {
      if (editingLesson) {
        await api.updateLesson(editingLesson.id, lessonForm);
        toast.success('Lezione aggiornata');
      } else {
        await api.createLesson(lessonForm);
        toast.success('Lezione creata');
      }
      setShowLessonModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteLesson = async (lessonId, e) => {
    if (e) e.stopPropagation();
    try {
      await api.deleteLesson(lessonId);
      toast.success('Lezione eliminata');
      setShowLessonModal(false);
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Student callbacks
  // Availability callbacks
  const openAvailabilityModal = (day) => {
    setAvailabilityDay(day);
    setDisabledSlots(availability[day] || []);
    setShowAvailabilityModal(true);
  };

  const saveAvailability = async () => {
    try {
      await api.updateAvailability({ day: availabilityDay, disabled_slots: disabledSlots });
      toast.success('Disponibilità aggiornata');
      setShowAvailabilityModal(false);
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const TAB_CONFIG = {
    schedule: { title: 'Orario Settimanale', subtitle: 'Visualizza il tuo calendario lezioni' },
    'lessons-list': { title: 'Lezioni', subtitle: 'Modifica giorno e orario delle lezioni' },
    students: { title: 'Gestione Alunni', subtitle: 'Visualizza e modifica i contatti dei tuoi alunni' },
    attendance: { title: 'Rilevazione Presenze', subtitle: 'Registra le presenze giornaliere per ogni lezione' },
    payments: { title: 'Addebiti', subtitle: 'Visualizza gli addebiti e i pagamenti dei tuoi alunni' },
    receipts: { title: 'Ricevute', subtitle: 'Ricevute emesse a tuo nome' },
    statistics: { title: 'Statistiche Presenze', subtitle: 'Conteggio presenze per alunno' },
    documents: { title: 'Documenti', subtitle: 'Visualizza e scarica i tuoi documenti' },
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#52525B]">Caricamento...</div></div>;
  }

  function TeacherDocuments() {
    const [docs, setDocs] = useState([]);
    const [docsLoading, setDocsLoading] = useState(true);

    useEffect(() => {
      setDocsLoading(true);
      api.getMyDocuments().then(res => setDocs(res.data)).catch(() => toast.error('Errore caricamento documenti')).finally(() => setDocsLoading(false));
    }, []);

    const handleDownload = async (doc) => {
      try {
        const res = await api.downloadMyDocument(doc.id);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.original_name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch { toast.error('Errore download'); }
    };

    const fileIcon = (name) => {
      const ext = (name || '').split('.').pop().toLowerCase();
      if (['pdf'].includes(ext)) return <FilePdf size={20} className="text-red-500" />;
      if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileXls size={20} className="text-green-600" />;
      if (['doc', 'docx'].includes(ext)) return <FileDoc size={20} className="text-blue-600" />;
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <FileImage size={20} className="text-purple-500" />;
      if (['zip', 'rar', '7z'].includes(ext)) return <FileZip size={20} className="text-amber-600" />;
      return <File size={20} className="text-[#52525B]" />;
    };

    const formatSize = (bytes) => {
      if (!bytes) return '-';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1048576).toFixed(1)} MB`;
    };

    const categoryColors = {
      'Fattura': 'bg-blue-100 text-blue-700',
      'Comunicazione': 'bg-green-100 text-green-700',
      'Report': 'bg-purple-100 text-purple-700',
      'Modello Fiscale': 'bg-amber-100 text-amber-700',
      'Contratto': 'bg-red-100 text-red-700',
      'Altro': 'bg-gray-100 text-gray-600',
    };

    return (
      <div className="space-y-4">
        <div className="bg-white border border-[#E4E4E7] rounded-sm px-4 py-2 text-sm inline-block" data-testid="teacher-docs-count">
          <span className="text-[#52525B]">Documenti disponibili:</span> <span className="font-bold font-['Work_Sans']">{docs.length}</span>
        </div>
        {docsLoading ? (
          <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
            <FolderOpen size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Nessun documento disponibile</p>
            <p className="text-[#A1A1AA] text-sm mt-1">I documenti caricati dall'amministrazione saranno visibili qui</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="teacher-documents-table">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">File</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Categoria</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Descrizione</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Dimensione</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Scarica</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  const dt = new Date(doc.uploaded_at);
                  const dateStr = dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
                  return (
                    <tr key={doc.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`teacher-doc-row-${doc.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {fileIcon(doc.original_name)}
                          <span className="text-sm font-medium truncate max-w-[250px]">{doc.original_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {doc.category ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${categoryColors[doc.category] || 'bg-gray-100 text-gray-600'}`}>
                            {doc.category}
                          </span>
                        ) : <span className="text-[#D4D4D8] text-sm">-</span>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[#52525B] max-w-[200px] truncate">{doc.description || <span className="text-[#D4D4D8]">-</span>}</td>
                      <td className="px-4 py-2.5 text-sm text-right text-[#52525B] font-['Work_Sans']">{formatSize(doc.file_size)}</td>
                      <td className="px-4 py-2.5 text-xs text-[#52525B] whitespace-nowrap">{dateStr}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                          title="Scarica"
                          data-testid={`teacher-download-doc-${doc.id}`}
                        >
                          <DownloadSimple size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {/* Mobile hamburger */}
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="teacher-mobile-menu-btn"
      >
        <ListIcon size={24} weight="bold" />
      </button>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar bg-[#2D2A4A] ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="mb-8">
          <img src={LOGO_URL} alt="Fiori Musicali" className="w-full max-w-[180px] h-auto" />
        </div>
        <div className="mb-4 px-1">
          <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold mb-1.5 px-2">Anno</div>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            className="w-full px-3 py-1.5 rounded-sm text-sm font-semibold bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-1 focus:ring-white/40 cursor-pointer"
            data-testid="teacher-year-select"
          >
            {availableYears.map(y => (
              <option key={y} value={y} className="text-[#2D2A4A]">{y} / {(y + 1).toString().slice(-2)}</option>
            ))}
          </select>
        </div>
        <nav className="flex-1 space-y-1">
          {[
            { key: 'schedule', icon: CalendarBlank, label: 'Orario' },
            { key: 'students', icon: Users, label: 'Alunni' },
            { key: 'attendance', icon: ClipboardText, label: 'Rilevazione Presenze' },
            { key: 'statistics', icon: ChartBar, label: 'Statistiche Presenze' },
            { key: 'payments', icon: CurrencyEur, label: 'Addebiti' },
            { key: 'receipts', icon: Receipt, label: 'Ricevute' },
            { key: 'documents', icon: FolderOpen, label: 'Documenti' },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setSidebarOpen(false); }}
              className={`nav-item w-full ${activeTab === key ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
              data-testid={`nav-${key}`}
            >
              <Icon size={20} />
              {label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Top bar - logout only */}
        <div className="flex items-center justify-end mb-2 animate-fade-in">
          <button onClick={handleLogout} className="flex items-center gap-1.5 bg-[#0A0A0A] text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-[#1a1a1a] transition-colors" data-testid="logout-button">
            <SignOut size={18} />
            Esci
          </button>
        </div>
        <div className="mb-6 animate-fade-in">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight">{TAB_CONFIG[activeTab].title}</h1>
            {selectedYear && (activeTab === 'attendance' || activeTab === 'statistics') && (
              <span className="inline-flex items-center gap-1.5 bg-[#2D2A4A] text-white text-sm font-bold px-3 py-1.5 rounded-sm" data-testid="teacher-page-year-badge">
                <span className="text-[10px] uppercase tracking-wider text-white/70">Anno</span>
                <span>{selectedYear} / {(selectedYear + 1).toString().slice(-2)}</span>
              </span>
            )}
          </div>
          <p className="text-[#52525B] text-sm">{TAB_CONFIG[activeTab].subtitle}</p>
        </div>

        {/* Stats */}
        {activeTab === 'students' || activeTab === 'lessons-list' ? (
          <div className="flex justify-end gap-4 mb-6 animate-fade-in animate-delay-1">
            <div className="stat-card"><h3>{activeTab === 'lessons-list' ? 'Lezioni' : 'Alunni'}</h3><p className="value">{activeTab === 'lessons-list' ? lessons.length : students.length}</p></div>
            <div className="bg-[#0A0A0A] text-white rounded-sm p-4" data-testid="teacher-info-card">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-1">Insegnante</h3>
              <p className="text-xl font-bold font-['Work_Sans']">{user?.name || '-'}</p>
              <p className="text-sm text-white/70">{user?.instrument || ''}</p>
            </div>
          </div>
        ) : (activeTab === 'attendance') ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 animate-fade-in animate-delay-1">
            <div className="stat-card"><h3>Lezioni Totali</h3><p className="value">{attendanceTotal}</p></div>
            <div className="stat-card"><h3>Alunni</h3><p className="value">{students.length}</p></div>
            <div className="stat-card">
              <h3>Ore Settimanali</h3>
              <p className="value">
                {lessons.reduce((acc, l) => acc + (timeToSlotIndex(l.end_time) - timeToSlotIndex(l.start_time)) * 0.25, 0).toFixed(1)}
              </p>
            </div>
            <div className="bg-[#0A0A0A] text-white rounded-sm p-4" data-testid="teacher-info-card">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-1">Insegnante</h3>
              <p className="text-xl font-bold font-['Work_Sans']">{user?.name || '-'}</p>
              <p className="text-sm text-white/70">{user?.instrument || ''}</p>
            </div>
          </div>
        ) : (
          <div className="flex justify-end mb-4 animate-fade-in animate-delay-1">
            <div className="bg-[#0A0A0A] text-white rounded-sm px-3 py-1.5 flex items-baseline gap-2" data-testid="teacher-info-card">
              <span className="text-[10px] uppercase tracking-wider text-white/60">Insegnante</span>
              <span className="text-sm font-bold font-['Work_Sans'] uppercase">{user?.name || '-'}</span>
              {user?.instrument && <span className="text-xs text-white/70">· {user.instrument}</span>}
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="animate-fade-in animate-delay-2">
          {activeTab === 'schedule' && (
            <>
              <div className="mb-2 flex items-center justify-between gap-3 flex-wrap text-[11px] text-[#52525B] bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm px-3 py-1.5" data-testid="orario-status-legenda">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-semibold uppercase text-[10px] tracking-wider text-[#2D2A4A]">Legenda stato:</span>
                  <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-5 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">X</span> in attesa pagamento</span>
                  <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-5 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">I</span> iscrizione pagata</span>
                  <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-6 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">R1</span> rata 1 pagata</span>
                  <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-6 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">R2</span> rata 2 pagata</span>
                  <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-6 h-4 rounded bg-green-500 text-white text-[9px] font-extrabold">R3</span> rata 3 pagata</span>
                </div>
                <button
                  onClick={() => scheduleGridRef.current?.print()}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-[#2D2A4A] bg-white border border-[#2D2A4A] rounded-sm hover:bg-[#2D2A4A] hover:text-white transition-colors shrink-0 no-print"
                  data-testid="print-schedule-button-teacher"
                >
                  Stampa Orario
                </button>
              </div>
              <div className="schedule-grid-wrapper">
                <ScheduleGrid
                  ref={scheduleGridRef}
                  hideInternalPrintButton
                  lessons={lessons}
                  availability={availability}
                  onCellClick={null}
                  onEditLesson={openEditLesson}
                  onDeleteLesson={null}
                  onAvailabilityClick={openAvailabilityModal}
                  loadData={loadData}
                  onToggleProva={async (lessonId, is_prova) => {
                    try {
                      await api.teacherSetLessonProva(lessonId, is_prova);
                      toast.success(is_prova ? 'Flag PROVA attivato' : 'Iscrizione confermata: flag PROVA rimosso');
                      loadData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
                    }
                  }}
                  teacherName={user?.name}
                  teacherInstrument={user?.instrument}
                />
              </div>
            </>
          )}
          {activeTab === 'students' && (
            <StudentsList
              students={students}
              lessons={lessons}
              onUpdate={loadData}
            />
          )}
          {activeTab === 'lessons-list' && (
            <LessonsList
              lessons={lessons}
              onUpdate={loadData}
            />
          )}
          {activeTab === 'attendance' && (
            <AttendanceView lessons={lessons} students={students} onTotalChange={setAttendanceTotal} anno={selectedYear} />
          )}
          {activeTab === 'payments' && (
            <PaymentsView onTotalChange={setPaymentsTotal} anno={selectedYear} />
          )}
          {activeTab === 'receipts' && (
            <ReceiptsView anno={selectedYear} />
          )}
          {activeTab === 'statistics' && (
            <StatisticsView anno={selectedYear} />
          )}
          {activeTab === 'documents' && (
            <TeacherDocuments />
          )}
        </div>
      </main>

      {/* Lesson Modal - Teacher can only edit day/time */}
      <Dialog open={showLessonModal} onOpenChange={setShowLessonModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Modifica Lezione</DialogTitle>
            <DialogDescription>Modifica giorno e orario della lezione</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveLesson} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Alunno</Label>
              <div className="h-10 px-3 py-2 border border-[#E4E4E7] rounded-sm bg-[#F4F4F5] text-sm uppercase flex items-center" data-testid="lesson-student-readonly">
                {editingLesson?.student_name || 'N/A'}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Giorno</Label>
              <Select value={lessonForm.day.toString()} onValueChange={(v) => setLessonForm({...lessonForm, day: parseInt(v)})}>
                <SelectTrigger className="rounded-sm" data-testid="lesson-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PLANNER_DAYS.map((day, i) => <SelectItem key={i} value={i.toString()}>{day}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Ora Inizio</Label>
                <Select value={lessonForm.start_time} onValueChange={(v) => setLessonForm({...lessonForm, start_time: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="lesson-start-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Ora Fine</Label>
                <Select value={lessonForm.end_time} onValueChange={(v) => setLessonForm({...lessonForm, end_time: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="lesson-end-time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    <SelectItem value="20:00">20:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowLessonModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" className="bg-[#0A0A0A] rounded-sm" data-testid="save-lesson-button">Aggiorna</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Availability Modal */}
      <Dialog open={showAvailabilityModal} onOpenChange={setShowAvailabilityModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Disponibilità - {DAYS[availabilityDay]}</DialogTitle>
            <DialogDescription>Gestisci la tua disponibilità per questo giorno</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-[#52525B] mb-2">Clicca sugli slot per disabilitarli. Gli slot rossi non saranno disponibili per le lezioni.</p>
            {(() => {
              const dayLessons = lessons.filter(l => l.day === availabilityDay);
              const occupiedSlots = new Set();
              dayLessons.forEach(l => {
                const startIdx = TIME_SLOTS.indexOf(l.start_time);
                const endIdx = TIME_SLOTS.indexOf(l.end_time);
                if (startIdx >= 0 && endIdx >= 0) for (let i = startIdx; i < endIdx; i++) occupiedSlots.add(TIME_SLOTS[i]);
                else if (startIdx >= 0) occupiedSlots.add(TIME_SLOTS[startIdx]);
              });
              const freeSlots = TIME_SLOTS.filter(s => !occupiedSlots.has(s));
              return (<>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button type="button" size="sm" className="rounded-sm text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="disable-all-day" onClick={() => setDisabledSlots([...TIME_SLOTS])}>Disabilita Tutta la Giornata</Button>
                  <Button type="button" size="sm" className="rounded-sm text-xs bg-amber-500 hover:bg-amber-600 text-white" data-testid="disable-free-slots" onClick={() => setDisabledSlots(prev => [...new Set([...prev, ...freeSlots])])}>Disabilita Slot Non Occupati</Button>
                  <Button type="button" size="sm" className="rounded-sm text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="enable-all-day" onClick={() => setDisabledSlots([])}>Abilita Tutta la Giornata</Button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {TIME_SLOTS.map(slot => {
                    const isOccupied = occupiedSlots.has(slot);
                    return (
                      <button key={slot} type="button" onClick={() => setDisabledSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot])}
                        className={`p-2 text-sm rounded border transition-colors ${disabledSlots.includes(slot) ? 'bg-red-100 text-red-700 border-red-300 font-medium' : isOccupied ? 'bg-blue-50 text-blue-700 border-blue-200 font-medium' : 'bg-white text-[#0A0A0A] border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
                        data-testid={`availability-slot-${slot}`}
                      >
                        {slot}{isOccupied ? ' *' : ''}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#A1A1AA] mt-1">* = slot con lezione</p>
              </>);
            })()}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowAvailabilityModal(false)} className="rounded-sm">Annulla</Button>
            <Button onClick={saveAvailability} className="bg-[#0A0A0A] rounded-sm" data-testid="save-availability-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showSendEmailModal} onOpenChange={setShowSendEmailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">{sendEmailStep === 'preview' ? 'Anteprima Email' : `Invia Email a ${sendEmailStudent?.name}`}</DialogTitle>
          </DialogHeader>
          {sendEmailStep === 'compose' ? (
          <div className="space-y-4">
            {sendEmailStudent && (
              <div className="text-xs text-[#52525B] bg-[#F4F4F5] p-2 rounded-sm">
                <div>Email 1: <strong>{sendEmailStudent.email || 'N/A'}</strong></div>
                {sendEmailStudent.email2 && <div>Email 2: <strong>{sendEmailStudent.email2}</strong></div>}
              </div>
            )}
            {sendEmailStudent?.email2 && (
              <div className="flex items-center gap-2">
                <input type="checkbox" id="t_send_both" checked={sendEmailForm.send_to_both} onChange={e => setSendEmailForm({...sendEmailForm, send_to_both: e.target.checked})} />
                <Label htmlFor="t_send_both" className="text-sm cursor-pointer">Invia a entrambi gli indirizzi</Label>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant={sendEmailMode === 'free' ? 'default' : 'outline'} size="sm" onClick={() => setSendEmailMode('free')} className="rounded-sm">Mail Libera</Button>
              <Button variant={sendEmailMode === 'template' ? 'default' : 'outline'} size="sm" onClick={() => setSendEmailMode('template')} className="rounded-sm">Da Modello</Button>
            </div>
            {sendEmailMode === 'free' ? (
              <div className="space-y-3">
                <div className="space-y-1"><Label className="label-overline">Oggetto</Label><Input value={sendEmailForm.subject} onChange={e => setSendEmailForm({...sendEmailForm, subject: e.target.value})} placeholder="Oggetto email" className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="label-overline">Testo</Label><textarea value={sendEmailForm.body} onChange={e => setSendEmailForm({...sendEmailForm, body: e.target.value})} placeholder="Scrivi il testo..." rows={5} className="w-full border border-[#E4E4E7] rounded-sm p-2 text-sm" /></div>
              </div>
            ) : (
              <div className="space-y-1"><Label className="label-overline">Seleziona Modello</Label>
                <Select value={sendEmailForm.template_id} onValueChange={v => setSendEmailForm({...sendEmailForm, template_id: v})}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Scegli modello..." /></SelectTrigger>
                  <SelectContent>{emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} {t.has_estratto_conto ? '(+ E.C.)' : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSendEmailModal(false)} className="rounded-sm">Annulla</Button>
              <Button onClick={() => setSendEmailStep('preview')} disabled={sendEmailMode === 'free' ? !sendEmailForm.subject : !sendEmailForm.template_id} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm">Anteprima</Button>
            </DialogFooter>
          </div>
          ) : (
          <div className="space-y-4">
            {(() => {
              const sel = sendEmailMode === 'template' ? emailTemplates.find(t => t.id === sendEmailForm.template_id) : null;
              const previewSubject = sel ? sel.subject : sendEmailForm.subject;
              const previewBody = sel ? sel.body : sendEmailForm.body;
              const hasEC = sel?.has_estratto_conto || false;
              const attachments = sel?.attachments || [];
              const recipients = [sendEmailStudent?.email];
              if (sendEmailForm.send_to_both && sendEmailStudent?.email2) recipients.push(sendEmailStudent.email2);
              return (<>
                <div className="text-xs text-[#52525B] space-y-1 bg-[#F4F4F5] p-2 rounded-sm">
                  <div><strong>A:</strong> {recipients.join(', ')}</div>
                </div>
                <div className="border border-[#E4E4E7] rounded-sm overflow-hidden">
                  <div className="bg-[#2D2A4A] text-white text-center py-3 text-sm font-semibold">Fiori Musicali</div>
                  <div className="p-4">
                    <div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Oggetto</div>
                    <div className="font-semibold text-sm mb-3">{previewSubject}</div>
                    <div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Testo</div>
                    <div className="text-sm whitespace-pre-wrap text-[#3F3F46]">{previewBody}</div>
                  </div>
                  {(hasEC || attachments.length > 0) && (
                    <div className="border-t border-[#E4E4E7] px-4 py-2 bg-[#FAFAFA]">
                      <div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Allegati</div>
                      {hasEC && <div className="text-xs flex items-center gap-1 text-amber-700 font-medium"><FileText size={14} /> Estratto Conto PDF</div>}
                      {attachments.map(a => <div key={a.id} className="text-xs flex items-center gap-1 text-[#52525B]"><Paperclip size={12} /> {a.filename}</div>)}
                    </div>
                  )}
                  <div className="bg-[#F4F4F5] text-center py-2 text-[10px] text-[#A1A1AA]">Fiori Musicali - Scuola di Musica</div>
                </div>
              </>);
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendEmailStep('compose')} className="rounded-sm">Modifica</Button>
              <Button onClick={handleTeacherSendEmail} disabled={sendingEmail} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm">
                {sendingEmail ? 'Invio...' : <><PaperPlaneTilt size={16} className="mr-1" /> Conferma Invio</>}
              </Button>
            </DialogFooter>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
